import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Repeat } from "lucide-react";
import { addDays, addMonths, addWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, format, isSameMonth } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import AppointmentFormDialog from "@/components/AppointmentFormDialog";
import { api, errMsg } from "@/lib/api";
import { dayKey, fmtTime, keyToDate, todayKey, money, SERVICE_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const PILL = {
  requested: "border-l-amber-500 bg-amber-50", confirmed: "border-l-sky-500 bg-sky-50", in_progress: "border-l-teal-600 bg-teal-50",
  completed: "border-l-green-600 bg-green-50", canceled: "border-l-red-400 bg-red-50 line-through opacity-70",
};
const k = (d) => format(d, "yyyy-MM-dd");

function daysFor(view, cursor) {
  const c = keyToDate(cursor);
  if (view === "day") return [c];
  if (view === "week") return eachDayOfInterval({ start: startOfWeek(c, { weekStartsOn: 1 }), end: endOfWeek(c, { weekStartsOn: 1 }) });
  return eachDayOfInterval({ start: startOfWeek(startOfMonth(c), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(c), { weekStartsOn: 1 }) });
}

function Pill({ a, tz, compact }) {
  return (
    <Link to={`/jobs/${a.id}`} data-testid={`appt-pill-${a.id}`} className={`block border-l-4 rounded-md px-2 py-1.5 text-xs hover:brightness-95 transition-[filter] ${PILL[a.status]}`}>
      <div className="font-semibold text-slate-900 flex items-center gap-1">{fmtTime(a.start, tz)}{a.series_id && <Repeat className="h-3 w-3 text-slate-500" />}</div>
      {!compact && <div className="text-slate-700 truncate">{a.client_name}</div>}
    </Link>
  );
}

function DayList({ items, tz }) {
  if (!items.length) return <p className="text-sm text-slate-500 py-8" data-testid="day-empty">No jobs scheduled for this day.</p>;
  return (
    <div className="space-y-3">
      {items.map((a) => (
        <Link key={a.id} to={`/jobs/${a.id}`} data-testid={`day-appt-${a.id}`} className={`flex items-center gap-4 border-l-4 rounded-lg p-4 bg-white border border-slate-200 ${PILL[a.status].split(" ")[0]}`}>
          <div className="w-20 shrink-0"><div className="font-display font-semibold text-slate-900">{fmtTime(a.start, tz)}</div><div className="text-xs text-slate-500">{fmtTime(a.end, tz)}</div></div>
          <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{a.client_name}</div><div className="text-xs text-slate-500 truncate">{SERVICE_LABELS[a.service_type]} · {a.address || "No address"} · {money(a.price)}</div></div>
          <StatusBadge status={a.status} />
        </Link>
      ))}
    </div>
  );
}

export default function Schedule() {
  const { tz, isManager } = useAuth();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(() => todayKey(tz));
  const [appts, setAppts] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");
  const days = useMemo(() => daysFor(view, cursor), [view, cursor]);

  const load = useCallback(() => {
    const start = addDays(days[0], -1).toISOString();
    const end = addDays(days[days.length - 1], 2).toISOString();
    api.get("/appointments", { params: { start, end } }).then((r) => setAppts(r.data)).catch((e) => setError(errMsg(e)));
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    (appts || []).forEach((a) => { (m[dayKey(a.start, tz)] ||= []).push(a); });
    return m;
  }, [appts, tz]);

  const shift = (n) => {
    const c = keyToDate(cursor);
    setCursor(k(view === "day" ? addDays(c, n) : view === "week" ? addWeeks(c, n) : addMonths(c, n)));
  };
  const c = keyToDate(cursor);
  const label = view === "day" ? format(c, "EEEE, MMM d, yyyy") : view === "week" ? `${format(days[0], "MMM d")} – ${format(days[6], "MMM d, yyyy")}` : format(c, "MMMM yyyy");
  const today = todayKey(tz);
  const onOpenChange = (v) => { setOpen(v); if (!v && params.get("new")) setParams({}); };

  return (
    <div data-testid="schedule-page">
      <PageHeader title="Schedule" subtitle={`Times shown in ${tz}`} actions={isManager && <Button className="h-11" onClick={() => setOpen(true)} data-testid="new-appointment-btn"><Plus className="h-4 w-4 mr-1" />New job</Button>} />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(-1)} data-testid="calendar-prev-btn" aria-label="Previous"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-11" onClick={() => setCursor(today)} data-testid="calendar-today-btn">Today</Button>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(1)} data-testid="calendar-next-btn" aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
          <span className="font-display font-semibold text-slate-900 ml-2" data-testid="calendar-label">{label}</span>
        </div>
        <Tabs value={view} onValueChange={setView} className="sm:ml-auto">
          <TabsList className="h-11">
            {["day", "week", "month"].map((v) => <TabsTrigger key={v} value={v} className="h-9 capitalize" data-testid={`view-${v}-tab`}>{v}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {!appts ? <Loading /> : view === "day" ? (
        <DayList items={byDay[cursor] || []} tz={tz} />
      ) : view === "week" ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="week-grid">
          {days.map((d) => (
            <div key={k(d)} className={`bg-white border rounded-lg p-2 min-h-[80px] md:min-h-[260px] ${k(d) === today ? "border-teal-600" : "border-slate-200"}`} data-testid={`week-day-${k(d)}`}>
              <button onClick={() => { setView("day"); setCursor(k(d)); }} className="text-xs font-medium text-slate-500 mb-2 hover:text-teal-700 min-h-[28px]">{format(d, "EEE d")}</button>
              <div className="space-y-1.5">{(byDay[k(d)] || []).map((a) => <Pill key={a.id} a={a} tz={tz} />)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="month-grid">
          <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const items = byDay[k(d)] || [];
              return (
                <button key={k(d)} onClick={() => { setView("day"); setCursor(k(d)); }} data-testid={`month-day-${k(d)}`}
                  className={`text-left border-b border-r border-slate-100 p-1.5 min-h-[64px] md:min-h-[100px] hover:bg-slate-50 transition-colors ${isSameMonth(d, c) ? "" : "bg-slate-50/60 text-slate-400"}`}>
                  <div className={`text-xs font-medium mb-1 ${k(d) === today ? "inline-flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-white" : ""}`}>{format(d, "d")}</div>
                  <div className="hidden md:block space-y-1">{items.slice(0, 3).map((a) => <div key={a.id} className={`truncate text-[11px] border-l-2 pl-1 ${PILL[a.status]}`}>{fmtTime(a.start, tz)} {a.client_name}</div>)}{items.length > 3 && <div className="text-[11px] text-slate-500">+{items.length - 3} more</div>}</div>
                  {items.length > 0 && <div className="md:hidden text-[11px] font-semibold text-teal-700">{items.length} job{items.length > 1 ? "s" : ""}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {isManager && <AppointmentFormDialog open={open} onOpenChange={onOpenChange} defaultStart={view === "day" ? `${cursor}T09:00` : undefined} onSaved={load} />}
    </div>
  );
}
